import classNames from 'classnames';
import { differenceInSeconds, parseISO } from 'date-fns';
import { compact } from 'lodash-es';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { SelectedCheckmark } from '../../../components';
import { FoodLine, State, WarningItem } from '../../../models';
import { userSelectors } from '../../../redux';
import Badge from './Badge';
import LiveGastronom from './LiveGastronom';

interface Props {
  isSelected: boolean;
  foodLine: FoodLine;
  isCollapsed: boolean;
  setSelection: (id: string) => void;
}

const FoodLinePreview: FC<Props> = ({ isSelected, foodLine, isCollapsed, setSelection }) => {
  const warningTime: number = useSelector(userSelectors.getWarningTime);

  const [seconds, setSeconds] = useState(0);
  const [warnings, setWarnings] = useState<WarningItem[]>([]);

  const hasWarnings = !!warnings.find(r => r.state === State.WARNING);
  const hasOvertime = !!warnings.find(r => r.state === State.OVERTIME);
  const state = hasOvertime ? State.OVERTIME : hasWarnings ? State.WARNING : State.TIMER;

  useEffect(() => {
    const timerId = setInterval(() => {
      setSeconds(new Date().getSeconds());
    }, 1000);

    return () => {
      clearInterval(timerId);
    };
  }, []);

  useEffect(() => {
    if (!warningTime) {
      return;
    }
    const tempWarnings = compact(
      foodLine.foodTrays.map(foodTray => {
        if (!foodTray?.foodItem?.enteredAt) {
          return null;
        }
        const foodType = foodTray.foodItem.overrideFoodType || foodTray.defaultFoodType;
        const time =
          parseFloat(foodType.maxHoldTime) * 60 -
          Math.abs(differenceInSeconds(new Date(), parseISO(foodTray.foodItem.enteredAt)));

        if (time < 0) {
          return { state: State.OVERTIME, id: foodTray.foodItem.id }; // red
        } else if (time <= warningTime * 60) {
          return { state: State.WARNING, id: foodTray.foodItem.id }; // orange
        }
        return null; // no warning
      }),
    );
    setWarnings(tempWarnings);
  }, [seconds]);

  const classes = classNames('preview', {
    active: isSelected,
    'preview-overtime': hasOvertime,
    'preview-warning': hasWarnings && !hasOvertime,
  });
  return (
    <div className='preview-wrapper' key={`preview-${foodLine.id}`}>
      <div className={classes} onClick={() => setSelection(foodLine.id)}>
        {isSelected && <SelectedCheckmark />}
        {!isCollapsed && <LiveGastronom foodTrays={foodLine?.foodTrays || []} />}
        <p className='title'>
          {foodLine.name} {warnings.length > 0 && <Badge number={warnings.length} state={state} />}
        </p>
      </div>
    </div>
  );
};

export default FoodLinePreview;
